import { readContract } from '@wagmi/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { zeroHash } from 'viem'
import { useAccount } from 'wagmi'

import { ELECTIONS_ADDRESS, VOTE_ADDRESS } from '@/constants/blockchain'
import OMMAElection from '@/contracts/OMMAElection'
import Vote from '@/contracts/Vote'
import { useCustomContractRead, useCustomContractReadBalance } from '@/hooks/useCustomContract'
import { getWagmiConfig, l1NativeConfig, l1NativeWagmiConfig } from '@/plugins/auth/config'
import { CustomError, parseError } from '@/utils/helpers'
import { emptyBalance, ProcessedBalance } from '@/utils/utils'

import { useChainValues } from './useChain'

export type CandidateStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'REMOVED'

export type NominationData = {
  nominee: string
  nominator: string
  owner: string
  status: CandidateStatus
  cid: string
}

// Cast Votes
export interface CastVotesPayload {
  votes: {
    candidate: string
    amount: string
  }[]
  signature: string
}

export interface CastVotesResponse {
  valid: boolean
  error?: string
}

// Map candidate status number to string
export const CandidateStatusMap: Record<number, CandidateStatus> = {
  0: 'PENDING',
  1: 'ACCEPTED',
  2: 'REJECTED',
  3: 'REMOVED',
}

export type ElectionData = {
  idx: number
  nominationTime: number
  campaignTime: number
  votingTime: number
  endTime: number
}

export const mockNominations = [
  '0x79C5d517f87e690335cc9182459009D6955c058B',
  '0xbabe715c99baa348cdaa4488b63990996382cd50',
  '0xDc84F196C32AC5B98fB717f4662a23e7Fef902D7',
  '0x8db97C7cEcE249c2b98bDC0226Cc4C2A57BF52FC',
  '0x14cfdb4e0e9ca02f5b8e90b4e813510138eb97d7',
  '0xfd13564fe6593b04cd48dc35a92853323660d92f',
  '0x017dDBE44B7D9cAe5E7Bf0fDC7a26125a42b2C95',
  '0x77e7a023c90b8fdD6a284Ee8208482cc809125Ca',
  '0x3e0fe0Ee1BF683F60e1193768C9283DCA7469DD8',
  '0xad2fa67789285e9b8ebde3aac815b9d9bbc8857d',
  '0x9346f1d460cBC0fcA01997F1E5940fD55675173d',
  '0xbdd87538521123603205c2d14cf566851f244983',
]
const mockNominationData = mockNominations.map(nominee => ({
  nominee,
  nominator: nominee,
  owner: nominee,
  status: 'PENDING' as CandidateStatus,
  cid: '',
}))

// MOCK_ELECTIONS: change mock true/false here
export const mock = false

export type GovernanceHook = {
  vote: ProcessedBalance
  hasNominated: boolean
  isAdmin: boolean
  minVoteBalance: ProcessedBalance
  electionData: ElectionData
  nominationData: NominationData[]
  isNominee: (address: string) => boolean
  hasBeenNominated: (address: string) => boolean
  nomineeApplication: (address: string) => string
  isNomineePending: (address: string) => boolean
  nomineesRejected: (address: string) => number
  nomineesAccepted: (address: string) => number
  isLoading: boolean
  error: CustomError
}

export const EmptyGovernance: GovernanceHook = {
  vote: emptyBalance,
  hasNominated: false,
  isAdmin: false,
  minVoteBalance: emptyBalance,
  electionData: {
    idx: 0,
    nominationTime: 0,
    campaignTime: 0,
    votingTime: 0,
    endTime: 0,
  },
  nominationData: [],
  isNominee: () => false,
  hasBeenNominated: () => false,
  nomineeApplication: () => '',
  isNomineePending: () => false,
  nomineesRejected: () => 0,
  nomineesAccepted: () => 0,
  isLoading: false,
  error: {
    call: '',
    msg: undefined,
    isError: false,
  },
}

export const useGetNominationData = (blockNumber: number, candidates?: string[]) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [nominationData, setNominationData] = useState<NominationData[]>([])
  const config = getWagmiConfig()

  // Get rewards in batches if necessary
  const getNominators = async (cands: string[]): Promise<void> => {
    try {
      setIsLoading(true)
      const values = await Promise.all(
        cands.map(candidate =>
          readContract(config, {
            chainId: l1NativeConfig.chainId,
            address: ELECTIONS_ADDRESS,
            abi: OMMAElection,
            functionName: 'candidates',
            args: [candidate as `0x${string}`],
          })
        )
      )
      // Combine data
      const data = cands.map((candidate, idx) => ({
        nominee: candidate,
        nominator: values[idx][0],
        owner: values[idx][1],
        status: CandidateStatusMap[values[idx][2]],
        cid: values[idx][3],
      }))
      setNominationData(data)
      setIsError(false)
    } catch (error: any) {
      setIsError(true)
    }
    setIsLoading(false)
  }

  // Fetch on new blocks or changes to candidates
  useEffect(() => {
    if (candidates) {
      getNominators(candidates)
    }
  }, [blockNumber, candidates])

  return {
    nominationData,
    isLoading,
    error: { call: 'getNominationData', msg: isError ? '' : undefined, isError },
  }
}

export const useGovernance = (): GovernanceHook => {
  const { address } = useAccount()

  // Get block number
  const { blockNumber } = useChainValues(l1NativeWagmiConfig.id)

  // User vote balance
  const {
    value: vote,
    isLoading: isLoadingVote,
    error: errorVote,
  } = useCustomContractReadBalance(
    {
      chainId: l1NativeWagmiConfig.id,
      address: VOTE_ADDRESS,
      args: [address as `0x${string}`],
      abi: Vote,
      functionName: 'balanceOf',
    },
    blockNumber,
    !!address
  )

  // Get if user already nominated a candidate
  const {
    value: hasNominated,
    isLoading: isLoadingHasNominated,
    error: errorHasNominated,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      args: [address as `0x${string}`],
      abi: OMMAElection,
      functionName: 'hasNominated',
    },
    blockNumber,
    !!address
  )

  // Check if user is admin of the contract
  const {
    value: isAdmin,
    isLoading: isLoadingIsAdmin,
    error: errorIsAdmin,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      args: [zeroHash, address as `0x${string}`],
      abi: OMMAElection,
      functionName: 'hasRole',
    },
    blockNumber,
    !!address
  )

  // Get minimum vote balance
  const {
    value: minVoteBalance,
    isLoading: isLoadingMinVoteBalance,
    error: errorMinVoteBalance,
  } = useCustomContractReadBalance(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      args: [],
      abi: OMMAElection,
      functionName: 'minVoteBalance',
    },
    blockNumber
  )

  // Get current election
  const {
    value: currentElection,
    isLoading: isLoadingCurrentElection,
    error: errorCurrentElection,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      args: [],
      abi: OMMAElection,
      functionName: 'currentElection',
    },
    blockNumber
  )
  const electionData = useMemo(() => {
    let data: ElectionData = {
      idx: 0,
      nominationTime: 0,
      campaignTime: 0,
      votingTime: 0,
      endTime: 0,
    }
    if (currentElection) {
      data = {
        idx: parseInt(currentElection[0].toString(), 10),
        nominationTime: parseInt(currentElection[1].toString(), 10),
        campaignTime: parseInt(currentElection[2].toString(), 10),
        votingTime: parseInt(currentElection[3].toString(), 10),
        endTime: parseInt(currentElection[4].toString(), 10),
      }
    }
    return data
  }, [currentElection])

  // Get candidates
  const {
    value: candidatesRaw,
    isLoading: isLoadingCandidates,
    error: errorCandidates,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      args: [],
      abi: OMMAElection,
      functionName: 'getNominatedCandidates',
    },
    blockNumber
  )
  const candidates = useMemo(
    () => (candidatesRaw ? candidatesRaw.map(cand => cand as string) : []),
    [candidatesRaw]
  )

  // Build nomination data list
  const {
    nominationData,
    isLoading: isLoadingNominationData,
    error: errorNominationData,
  } = useGetNominationData(blockNumber, candidates)

  // Check if a user is a nominee and accepted nomination
  const isNominee = useCallback(
    (addr: string) => {
      const nom = nominationData.find(({ nominee }) => nominee.toLowerCase() === addr.toLowerCase())
      if (!nom) return false
      return nom.status === 'ACCEPTED'
    },
    [nominationData]
  )

  // Check if a candidate nominated by address was rejected
  const nomineesRejected = useCallback(
    (addr: string) => {
      // Find all nominations made by the current user
      const myNominations = nominationData.filter(
        cand => cand.nominator.toLowerCase() === addr.toLowerCase()
      )

      // Return if any of the nominations are rejected
      return myNominations.filter(nom => nom.status === 'REJECTED').length
    },
    [nominationData]
  )

  // Check if a condidate nominated by address was accepted
  const nomineesAccepted = useCallback(
    (addr: string) => {
      // Find all nominations made by the current user
      const myNominations = nominationData.filter(
        cand => cand.nominator.toLowerCase() === addr.toLowerCase()
      )

      // Return if any of the nominations are accepted
      return myNominations.filter(nom => nom.status === 'ACCEPTED').length
    },
    [nominationData]
  )
  // Check if a user has been nominated by someone
  const hasBeenNominated = useCallback(
    (addr: string) => {
      const nom = nominationData.find(({ nominee }) => nominee.toLowerCase() === addr.toLowerCase())
      if (!nom) return false
      return true
    },
    [nominationData]
  )

  // Check if a user is a nominee and still needs to take action
  const isNomineePending = useCallback(
    (addr: string) => {
      const nom = nominationData.find(({ nominee }) => nominee.toLowerCase() === addr.toLowerCase())
      if (!nom) return false
      return nom.status === 'PENDING'
    },
    [nominationData]
  )

  // Get application CID for a nominee
  const nomineeApplication = useCallback(
    (nomAddr: string) => {
      const nomData = nominationData.find(({ nominee }) => nominee === nomAddr)
      if (!nomData) return ''
      return nomData.cid
    },
    [nominationData]
  )

  // Loading
  const isLoading = useMemo(
    () =>
      isLoadingVote ||
      isLoadingHasNominated ||
      isLoadingIsAdmin ||
      isLoadingMinVoteBalance ||
      isLoadingCurrentElection ||
      isLoadingCandidates ||
      isLoadingNominationData,
    [
      isLoadingVote,
      isLoadingHasNominated,
      isLoadingIsAdmin,
      isLoadingMinVoteBalance,
      isLoadingCurrentElection,
      isLoadingCandidates,
      isLoadingNominationData,
    ]
  )

  // Error
  const errors = [
    errorVote,
    errorHasNominated,
    errorIsAdmin,
    errorMinVoteBalance,
    errorCurrentElection,
    errorCandidates,
    errorNominationData,
  ]
  const error = useMemo(() => parseError(errors), errors)

  const returnData = useMemo(() => {
    const data = {
      vote,
      hasNominated: hasNominated || false,
      isAdmin: isAdmin || false,
      minVoteBalance,
      electionData,
      nominationData: mock ? mockNominationData : nominationData,
      isNominee,
      hasBeenNominated,
      isNomineePending,
      nomineeApplication,
      nomineesRejected,
      nomineesAccepted,
      isLoading,
      error: {
        call: `governance.${error.call}`,
        msg: error.msg,
        isError: error.isError,
      },
    }
    // If no address, reset user related data
    if (!address) {
      data.vote = emptyBalance
      data.hasNominated = false
      data.isAdmin = false
    }
    return data
  }, [
    vote,
    hasNominated,
    isAdmin,
    minVoteBalance,
    electionData,
    nominationData,
    isNominee,
    hasBeenNominated,
    isNomineePending,
    nomineeApplication,
    nomineesRejected,
    nomineesAccepted,
    isLoading,
    error,
    address,
  ])

  return returnData
}
